import {
	addMonths,
	eachDayOfInterval,
	endOfMonth,
	format,
	isSameDay,
	startOfMonth,
	subMonths,
} from 'date-fns';
import { ca } from 'date-fns/locale';
import { useCallback, useMemo, useState } from 'react';

import useFetch from '../../hooks/useFetch';
import { capitalize } from '../../utils/strings';

import * as S from './RocacorbaCalendar.styles';

interface MonthData {
	year: number;
	month: number;
}

const RocacorbaCalendar = () => {
	const [currentDate, setCurrentDate] = useState(new Date());

	// Helper functions to get month data for previous, current, and next months
	const getCurrentMonthData = useCallback((date: Date): MonthData => {
		return {
			year: date.getFullYear(),
			month: date.getMonth() + 1,
		};
	}, []);

	const getPreviousMonthData = useCallback((date: Date): MonthData => {
		const prevMonth = subMonths(date, 1);
		return {
			year: prevMonth.getFullYear(),
			month: prevMonth.getMonth() + 1,
		};
	}, []);

	const getNextMonthData = useCallback((date: Date): MonthData => {
		const nextMonth = addMonths(date, 1);
		return {
			year: nextMonth.getFullYear(),
			month: nextMonth.getMonth() + 1,
		};
	}, []);

	// Get data for current month
	const currentMonthData = useMemo(
		() => getCurrentMonthData(currentDate),
		[currentDate, getCurrentMonthData]
	);
	const { data: currentMonthEventDates } = useFetch<{ dates: string[] }>({
		queryKey: [
			'rocacorba-calendar',
			currentMonthData.year,
			currentMonthData.month,
		],
		url: `/api/calendari/rocacorba?year=${currentMonthData.year}&month=${currentMonthData.month}`,
		queryOptions: { staleTime: 1000 * 60 * 30 }, // 30 min — calendar dates are static
	});

	// Get data for previous month
	const prevMonthData = useMemo(
		() => getPreviousMonthData(currentDate),
		[currentDate, getPreviousMonthData]
	);
	const { data: prevMonthEventDates } = useFetch<{ dates: string[] }>({
		queryKey: [
			'rocacorba-calendar',
			prevMonthData.year,
			prevMonthData.month,
		],
		url: `/api/calendari/rocacorba?year=${prevMonthData.year}&month=${prevMonthData.month}`,
		queryOptions: { staleTime: 1000 * 60 * 30 }, // 30 min
	});

	// Get data for next month
	const nextMonthData = useMemo(
		() => getNextMonthData(currentDate),
		[currentDate, getNextMonthData]
	);
	const { data: nextMonthEventDates } = useFetch<{ dates: string[] }>({
		queryKey: [
			'rocacorba-calendar',
			nextMonthData.year,
			nextMonthData.month,
		],
		url: `/api/calendari/rocacorba?year=${nextMonthData.year}&month=${nextMonthData.month}`,
		queryOptions: { staleTime: 1000 * 60 * 30 }, // 30 min
	});

	// Combine all dates from the three months
	const parsedDates = useMemo(() => {
		const allDates = [
			...(currentMonthEventDates?.dates || []),
			...(prevMonthEventDates?.dates || []),
			...(nextMonthEventDates?.dates || []),
		];
		return allDates.map((date) => new Date(date));
	}, [currentMonthEventDates, prevMonthEventDates, nextMonthEventDates]);

	const start = startOfMonth(currentDate);
	const end = endOfMonth(currentDate);
	const days = eachDayOfInterval({ start, end });

	// Adjust startDay to start from Monday (0) instead of Sunday (0)
	const startDay = (start.getDay() + 6) % 7;
	const padding = Array.from({ length: startDay }, () => null);

	// Catalan weekdays starting with Monday
	const weekdays = ['Dl', 'Dt', 'Dc', 'Dj', 'Dv', 'Ds', 'Dg'];

	const handlePreviousMonth = () => {
		setCurrentDate(subMonths(currentDate, 1));
	};

	const handleNextMonth = () => {
		setCurrentDate(addMonths(currentDate, 1));
	};

	return (
		<S.CalendarContainer>
			{/* MonthHeader moved below DaysGrid */}
			<S.WeekDays>
				{weekdays.map((day) => (
					<S.WeekDay key={day}>{day}</S.WeekDay>
				))}
			</S.WeekDays>

			<S.DaysGrid>
				{padding.map((_, i) => (
					<div key={`pad-${i}`} />
				))}
				{days.map((day) => (
					<S.Day
						key={day.toISOString()}
						$isUnavailable={parsedDates.some((date: Date) =>
							isSameDay(date, day)
						)}
					>
						{format(day, 'd')}
					</S.Day>
				))}
			</S.DaysGrid>

			<S.MonthHeader>
				<S.ArrowButton onClick={handlePreviousMonth}>
					&larr;
				</S.ArrowButton>
				<S.MonthTitle>
					{capitalize(
						format(currentDate, 'MMMM yyyy', { locale: ca })
					)}
				</S.MonthTitle>
				<S.ArrowButton onClick={handleNextMonth}>&rarr;</S.ArrowButton>
			</S.MonthHeader>

		</S.CalendarContainer>
	);
};

export default RocacorbaCalendar;
